import { useMemo } from 'react';
import { fmtPrice, fmtPct, fmtCompact } from '../../utils/fmt';
import HomeModule, { CoinGeckoMark, trackHome } from './HomeModule';

/* 24 órás nyertesek / vesztesek likviditási szűrővel (3a):
   24ó forgalom ≥ 10 M $, piaci kap. rang ≤ 300, stabilcoinok és wrapped /
   staked / bridged tokenek nélkül. Ha a szűrés után kevés marad, annyit mutat —
   nem tölti fel illikviddel. */

export const MIN_VOLUME = 10e6;
export const MAX_RANK = 300;
const SHOW = 3;

const STABLES = new Set([
  'usdt', 'usdc', 'dai', 'fdusd', 'usde', 'usds', 'tusd', 'pyusd', 'usdd', 'frax', 'busd', 'gusd', 'lusd',
  'susd', 'crvusd', 'rlusd', 'usd1', 'usdtb', 'usd0', 'usdx', 'usdb', 'usdy', 'eurc', 'eurt', 'eurs', 'usdp',
  'gho', 'dola', 'mim', 'usdj', 'usdf', 'usda', 'srusd', 'ustb', 'buidl', 'bfusd', 'usdg', 'usdai',
]);
const WRAPPED = new Set([
  'wbtc', 'weth', 'wbnb', 'wsol', 'wavax', 'wmatic', 'wtrx', 'wbeth', 'steth', 'wsteth', 'weeth', 'eeth', 'reth',
  'cbbtc', 'cbeth', 'meth', 'cmeth', 'ezeth', 'rseth', 'jitosol', 'msol', 'bnsol', 'jupsol', 'bbsol', 'lbtc',
  'solvbtc', 'tbtc', 'sweth', 'oseth', 'lseth', 'sfrxeth', 'frxeth', 'susde', 'susds', 'sdai', 'stbtc', 'enzobtc',
  'clbtc', 'ebtc', 'pumpbtc', 'unibtc', 'fbtc', 'btcb', 'hbtc', 'renbtc', 'kbtc', 'wst', 'stkaave', 'syrupusdc',
]);
const WRAP_NAME = /\b(wrapped|staked|bridged|restaked|liquid staked|binance-peg)\b/i;

export function isLiquidMover(c) {
  const sym = String(c?.symbol || '').toLowerCase();
  if (!sym || STABLES.has(sym) || WRAPPED.has(sym) || WRAP_NAME.test(c?.name || '')) return false;
  // Unlisted stablecoins: dollar-pegged by name and price
  if (/usd|eur/i.test(sym) && Math.abs((c.current_price ?? 0) - 1) < 0.03) return false;
  if (!(c.total_volume >= MIN_VOLUME)) return false;
  if (!(c.market_cap_rank > 0 && c.market_cap_rank <= MAX_RANK)) return false;
  return typeof c.price_change_percentage_24h === 'number';
}

function Row({ c, onSelect }) {
  const chg = c.price_change_percentage_24h;
  return (
    <button type="button" className="hmv-row" onClick={() => onSelect(c)}>
      <img src={c.image} alt="" className="hmv-ic" onError={e => { e.target.style.visibility = 'hidden'; }} />
      <span className="hmv-sym">{c.symbol.toUpperCase()}</span>
      <span className="hmv-price num">{fmtPrice(c.current_price)}</span>
      <span className={`hmv-chg num ${chg >= 0 ? 'pos' : 'neg'}`}>{fmtPct(chg)}</span>
      <span className="hmv-vol num" title="24 órás forgalom">{fmtCompact(c.total_volume)}</span>
    </button>
  );
}

export default function HomeMovers({ coins, meta, user, onSelectCoin, onRetry, expectedMs, error }) {
  const { gainers, losers, pool } = useMemo(() => {
    const liquid = coins.filter(isLiquidMover);
    const sorted = [...liquid].sort((a, b) => b.price_change_percentage_24h - a.price_change_percentage_24h);
    const g = sorted.filter(c => c.price_change_percentage_24h > 0).slice(0, SHOW);
    const l = sorted.filter(c => c.price_change_percentage_24h < 0).reverse().slice(0, SHOW);
    return { gainers: g, losers: l, pool: liquid.length };
  }, [coins]);

  const select = (c) => { trackHome(user, 'home_module_interaction', { module: 'top_movers', action: 'select_coin' }); onSelectCoin?.(c, 'home_movers'); };

  return (
    <HomeModule
      id="top_movers" title="24 órás mozgók" user={user}
      info={`Csak likvid coinok: 24 órás forgalom legalább 10 M $, piaci kapitalizáció szerinti rang legfeljebb ${MAX_RANK}; stabilcoinok és wrapped/staked tokenek nélkül. A % mellett a 24 órás forgalom.`}
      source="CoinGecko" attr={<CoinGeckoMark />} at={meta?.at} expectedMs={expectedMs} stale={meta?.stale} error={(error && !coins.length) || !!meta?.error}
      errorText={meta?.error || 'A CoinGecko most nem elérhető'} onRetry={onRetry} className="hmv"
    >
      <div className="hmv-sub">▲ Nyertesek</div>
      {gainers.length ? gainers.map(c => <Row key={c.id} c={c} onSelect={select} />) : <div className="hmv-empty">{coins.length ? 'Nincs likvid emelkedő.' : '…'}</div>}
      <div className="hmv-sub">▼ Vesztesek</div>
      {losers.length ? losers.map(c => <Row key={c.id} c={c} onSelect={select} />) : <div className="hmv-empty">{coins.length ? 'Nincs likvid csökkenő.' : '…'}</div>}
      {coins.length > 0 && pool < 2 * SHOW && <div className="hmv-empty">Kevés coin felel meg a likviditási szűrőnek.</div>}
    </HomeModule>
  );
}
